/**
    * @description      : 
    * @author           : USER
    * @group            : 
    * @created          : 04/09/2025 - 19:03:08
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 04/09/2025
    * - Author          : USER
    * - Modification    : 
**/
// src/pages/Contact.js
import React from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { 
  FaEnvelope, 
  FaPhone, 
  FaMapMarkerAlt, 
  FaFacebook, 
  FaInstagram,
  FaTwitter,
  FaLinkedin,
  FaTiktok,
  FaPaperPlane,
  FaUser,
  FaBuilding
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import { oxford, learning, unihawk, toloita } from '../assets';
import { fetchSponsors } from '../services/api';

const SponsorsMini = () => {
  const [sponsors, setSponsors] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      const res = await fetchSponsors();
      if (!mounted) return;
      if (res.success) setSponsors(res.sponsors || []);
      setLoading(false);
    };
    load();
    return () => { mounted = false; };
  }, []);

  if (loading) return null;
  if (!sponsors || sponsors.length === 0) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-md">
      <h3 className="text-xl font-bold text-gray-900 mb-4">Partners</h3>
      <div className="grid grid-cols-3 gap-3">
        {sponsors.slice(0,6).map(s => (
          <a key={s._id} href={s.website || '#'} target="_blank" rel="noopener noreferrer" className="p-2 bg-gray-50 rounded-xl flex items-center justify-center">
            {s.logo ? <img src={s.logo} alt={s.name} className="max-h-10 object-contain" /> : <span className="text-sm text-gray-700">{s.name}</span>}
          </a>
        ))}
      </div>
    </div>
  );
};

const Contact = () => {
  const { register, handleSubmit, formState: { errors }, reset } = useForm();

  const onSubmit = async (data) => {
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    toast.success('Message sent successfully! We\'ll get back to you soon.');
    reset();
  };

  const contactInfo = [
    {
      icon: FaEnvelope,
      title: 'Email Us',
      content: 'info@africandebatechampions.org',
      link: 'mailto:info@africandebatechampions.org'
    },
    {
      icon: FaPhone,
      title: 'Call Us',
      content: '+254 718 290 692',
      link: 'tel: +254 726 174 421'
    },
    {
      icon: FaMapMarkerAlt,
      title: 'Visit Us',
      content: 'Jewel Complex next to TRM Mall on Thika Rd, 4th Floor Office No. 413.',
      link: null
    }
  ];

  const socialLinks = [
    {
      icon: FaFacebook,
      name: 'Facebook',
      url: 'https://www.facebook.com/africandebatechampions',
      color: 'text-blue-600 hover:text-blue-700'
    },
    {
      icon: FaInstagram,
      name: 'Instagram',
      url: 'https://www.instagram.com/african_child_debaters/',
      color: 'text-pink-600 hover:text-pink-700'
    },
    {
      icon: FaTwitter,
      name: 'Twitter',
      url: 'https://www.twitter.com/africandebaters',
      color: 'text-blue-400 hover:text-blue-500'
    },
    {
      icon: FaLinkedin,
      name: 'LinkedIn',
      url: 'https://www.linkedin.com/company/african-child-debaters',
      color: 'text-blue-700 hover:text-blue-800'
    },
    {
      icon: FaTiktok,
      name: 'TikTok',
      url: 'https://www.tiktok.com/@africandebaters',
      color: 'text-pink-500 hover:text-pink-600'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 pt-24">
      {/* Hero Section - Compact */}
      <section className="px-4 sm:px-6 lg:px-8 py-12">
        <div className="container mx-auto max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-block px-4 py-2 bg-green-100 rounded-full border border-green-200 mb-6">
              <span className="text-green-700 text-sm font-medium">Get In Touch</span>
            </div>

            <h1 className="text-4xl sm:text-5xl font-bold leading-tight mb-6">
              <span className="text-gray-900">Contact</span>
              <br />
              <span className="text-green-600 drop-shadow-sm">Us </span>{' '}
              <span className="text-yellow-600 drop-shadow-sm">Today.</span>{' '}
              <span className="text-red-600 drop-shadow-sm">Together.</span>
            </h1>
            
            <p className="text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto">
              Ready to transform your school's debate program? We'd love to hear from you and discuss how we can help develop the next generation of African leaders.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="px-4 sm:px-6 lg:px-8 pb-16">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* Contact Form - 2/3 width */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-2"
            >
              <div className="bg-white border border-gray-200 rounded-3xl p-8 shadow-lg">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Send us a Message</h2>
                
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Full Name</label>
                      <div className="relative">
                        <FaUser className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                          type="text"
                          placeholder="Your full name"
                          className="block w-full rounded-2xl bg-gray-50 border border-gray-300 focus:border-green-500 focus:ring-green-500/20 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 pl-12 pr-4 py-3"
                          {...register('name', { required: 'Name is required' })}
                        />
                      </div>
                      {errors.name && <p className="text-red-500 text-sm">{errors.name.message}</p>}
                    </div>
                    
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">School Name</label>
                      <div className="relative">
                        <FaBuilding className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                          type="text"
                          placeholder="Your school name"
                          className="block w-full rounded-2xl bg-gray-50 border border-gray-300 focus:border-green-500 focus:ring-green-500/20 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 pl-12 pr-4 py-3"
                          {...register('school', { required: 'School name is required' })}
                        />
                      </div>
                      {errors.school && <p className="text-red-500 text-sm">{errors.school.message}</p>}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Email Address</label>
                    <div className="relative">
                      <FaEnvelope className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="email"
                        placeholder="your.email@example.com"
                        className="block w-full rounded-2xl bg-gray-50 border border-gray-300 focus:border-green-500 focus:ring-green-500/20 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 pl-12 pr-4 py-3"
                        {...register('email', { 
                          required: 'Email is required',
                          pattern: {
                            value: /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/,
                            message: 'Please enter a valid email address'
                          }
                        })}
                      />
                    </div>
                    {errors.email && <p className="text-red-500 text-sm">{errors.email.message}</p>}
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Phone Number (Optional)</label>
                    <div className="relative">
                      <FaPhone className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="tel"
                        placeholder="+254 7XX XXX XXX"
                        className="block w-full rounded-2xl bg-gray-50 border border-gray-300 focus:border-green-500 focus:ring-green-500/20 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 pl-12 pr-4 py-3"
                        {...register('phone')}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Message</label>
                    <textarea
                      rows={5}
                      placeholder="Tell us about your school and how we can help develop your students' debate and leadership skills..."
                      className="block w-full rounded-2xl bg-gray-50 border border-gray-300 focus:border-green-500 focus:ring-green-500/20 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 px-4 py-3 resize-none"
                      {...register('message', { required: 'Message is required' })}
                    />
                    {errors.message && <p className="text-red-500 text-sm">{errors.message.message}</p>}
                  </div>

                  <button
                    type="submit"
                    className="w-full flex items-center justify-center px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-2xl font-semibold hover:scale-105 transition-all duration-300 hover:shadow-lg"
                  >
                    <FaPaperPlane className="mr-2" />
                    Send Message
                  </button>
                </form>
              </div>
            </motion.div>

            {/* Contact Info Sidebar - 1/3 width */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="space-y-6"
            >
              {/* Contact Information */}
              <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-md">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Contact Information</h3>
                <div className="space-y-6">
                  {contactInfo.map((info, index) => {
                    const Icon = info.icon;
                    return (
                      <div key={index} className="flex items-start space-x-4">
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                            <Icon className="w-5 h-5 text-green-600" />
                          </div>
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="text-gray-900 font-semibold text-sm mb-1">{info.title}</h4>
                          {info.link ? (
                            <a
                              href={info.link}
                              className="text-gray-600 hover:text-green-600 transition-colors text-sm break-all"
                            >
                              {info.content}
                            </a>
                          ) : (
                            <p className="text-gray-600 text-sm">{info.content}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Social Media */}
              <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-md">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Follow Us</h3>
                <div className="flex flex-wrap gap-3">
                  {socialLinks.map((social, index) => {
                    const Icon = social.icon;
                    return (
                      <a
                        key={index}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gray-100 ${social.color} hover:scale-110 transition-all duration-300 hover:shadow-md`}
                        title={social.name}
                      >
                        <Icon className="w-5 h-5" />
                      </a>
                    );
                  })}
                </div>
              </div>

              {/* Partners / Sponsors */}
              <SponsorsMini />

              {/* Office Hours */}
              <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-md">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Office Hours</h3>
                <div className="space-y-2 text-gray-600 text-sm">
                  <div className="flex justify-between">
                    <span>Monday - Friday:</span>
                    <span className="font-medium">8:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Saturday:</span>
                    <span className="font-medium">9:00 AM - 2:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sunday:</span>
                    <span className="font-medium text-red-500">Closed</span>
                  </div>
                </div>
              </div>

              {/* Partners */}
              <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Our Partners</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="relative">
                    <img 
                      src={learning} 
                      alt="A Lab Learning" 
                      className="w-full h-12 rounded-lg object-contain border border-gray-300 shadow-md bg-white p-2 transition-all duration-300 hover:shadow-green-500/30 hover:scale-105"
                      style={{
                        filter: 'drop-shadow(0 0 4px rgba(34, 197, 94, 0.2))'
                      }}
                    />
                  </div>
                  <div className="relative">
                    <img 
                      src={unihawk} 
                      alt="Unihawk" 
                      className="w-full h-12 rounded-lg object-contain border border-gray-300 shadow-md bg-white p-2 transition-all duration-300 hover:shadow-blue-500/30 hover:scale-105"
                      style={{
                        filter: 'drop-shadow(0 0 4px rgba(59, 130, 246, 0.2))'
                      }}
                    />
                  </div>
                  <div className="relative">
                    <img 
                      src={oxford} 
                      alt="Oxford Schools" 
                      className="w-full h-12 rounded-lg object-contain border border-gray-300 shadow-md bg-white p-2 transition-all duration-300 hover:shadow-purple-500/30 hover:scale-105"
                      style={{
                        filter: 'drop-shadow(0 0 4px rgba(147, 51, 234, 0.2))'
                      }}
                    />
                  </div>
                  <div className="relative">
                    <img 
                      src={toloita} 
                      alt="Toloita Technologies" 
                      className="w-full h-12 rounded-lg object-contain border border-gray-300 shadow-md bg-white p-2 transition-all duration-300 hover:shadow-purple-500/30 hover:scale-105"
                      style={{
                        filter: 'drop-shadow(0 0 4px rgba(147, 51, 234, 0.2))'
                      }}
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;